import { handbookMissTarget } from '@/walkthrough/learnerShowMe'
import {
  createRouter,
  createWebHistory,
  type NavigationGuard,
  type NavigationGuardWithThis,
  type RouteRecordRaw,
  START_LOCATION,
} from 'vue-router'
import { teacherLandingTarget } from '@/composables/teacherLanding'
import { useUserRole } from '@/composables/useUserRole'
import { checkSocialDoor, isSocialPath } from '@/composables/useSocialDoor'
import { isHeldPath } from '@/config/releaseHolds'
import { adminNextFromQuery } from '@/composables/useAdminGate'
import { isChunkLoadError, recoverFromChunkError } from './staleChunkError'
import { unregisterAllServiceWorkers, clearAllCaches } from '@/composables/useServiceWorkerSafety'
import { showChunkFailureNotice } from './chunkFailureNotice'
import { loadDeferredEnglish, dropInferredLocale } from '@/composables/useI18n'
import { ENGLISH_ONLY_PATH } from '@/utils/deepLinkLocale'
import { pickerFirstRedirect, pickerFirstActive, windowSignals, PICKER_FIRST_PATH } from '@/platform/pickerFirst'
import { installedSignedOutFirstLoad, installedSignInPending, isStandaloneWindow } from '@/platform/installedReturn'
import { followLearnLinkInApp } from '@/platform/learnLink'
// Build-time: seat/institutional purchase is a WEB-ONLY rail. In a store build
// this folds to false, the three upgrade routes below are never constructed,
// and UpgradeView is never imported — the destination does not exist in the
// artifact rather than being hidden behind a flag somebody can flip.
import { INSTITUTIONAL_PURCHASE_IN_BUILD, isClosedWebPurchasePath } from '@/platform/paymentRoute'
import { isClosedTutorLanePath, tutorLaneAvailable } from '@/platform/tutorLane'

// Breadcrumb for the LAST management surface a user was on (`teach` | `schools`).
// Solo tutors have no `educational_role`, so the role cache can't tell a tutor
// apart from a plain learner — which is why a tutor who hits /schools used to
// fall straight onto the member-facing "no school access" wall with no way back.
// We remember which dashboard they came from so the /schools guard can bounce a
// non-member back to THEIR surface (a tutor → /teach) instead of the wall.
const LAST_DASHBOARD_KEY = 'ssi-last-dashboard'

function rememberDashboard(kind: 'teach' | 'schools'): void {
  try {
    localStorage.setItem(LAST_DASHBOARD_KEY, kind)
  } catch {
    // localStorage unavailable — non-fatal, we just lose the breadcrumb
  }
}

// In the store app the tutor lane does not exist (platform/tutorLane.ts), so a
// 'teach' breadcrumb, left by a build from before it closed, names nowhere and
// reads as none. Every tutor destination below is computed from this one read,
// so each becomes the learner home rather than a hop the guard has to undo.
export function lastDashboard(): 'teach' | 'schools' | null {
  try {
    const v = localStorage.getItem(LAST_DASHBOARD_KEY)
    if (v === 'teach') return tutorLaneAvailable() ? v : null
    return v === 'schools' ? v : null
  } catch {
    return null
  }
}

/**
 * The route of the management surface this user last came from, or null for a
 * plain learner who has never had one.
 *
 * Owner ruling 2026-08-06: entering the player ALWAYS gives the immersive,
 * navless player ('/'). That leaves a teacher/tutor who launched self-practice
 * from their dashboard with no shell nav to get home, so App.vue uses this
 * breadcrumb to show the one low-emphasis AppEscape pill back to their surface.
 * A plain learner returns null here and sees the fully navless player.
 */
export function lastDashboardPath(): string | null {
  const kind = lastDashboard()
  if (kind === 'teach') return '/tutors/dashboard'
  if (kind === 'schools') return '/schools'
  return null
}

/**
 * Where a signed-in non-admin goes when a page is not for them: a teacher,
 * tutor or school leader to their own dashboard, a learner to the player.
 * A teacher or leader following an /admin, /intel or /methodology link used to
 * land in the learner player (job #663, finding 2 of the #658 route check).
 * /schools does the rest: a leader is carried on to their node home, a solo
 * tutor to /tutors/dashboard.
 */
export function staffHome(): string {
  const { hasSchoolRole } = useUserRole()
  if (hasSchoolRole.value) return '/schools'
  return lastDashboard() === 'teach' ? '/tutors/dashboard' : '/'
}

// The two EMBEDDED play routes — the player wrapped in a management shell.
// Since 2026-08-06 these exist for play-as-class ONLY (see the global guard at
// the bottom of this file).
const EMBEDDED_PLAY_ROUTE_NAMES = ['schools-play', 'teach-play']

// Lazy-loaded views
const PlayerContainer = () => import('@/containers/PlayerContainer.vue')
const SchoolsContainer = () => import('@/containers/SchoolsContainer.vue')
const TeachContainer = () => import('@/containers/TeachContainer.vue')
const AdminContainer = () => import('@/containers/AdminContainer.vue')
const AdminSchoolsContainer = () => import('@/containers/AdminSchoolsContainer.vue')
const AdminGroupContainer = () => import('@/containers/AdminGroupContainer.vue')
const MethodologyContainer = () => import('@/containers/MethodologyContainer.vue')
import { QUESTIONS, RETIRED_QUESTIONS } from '@/intel/questions'
import { FOSSILS, fossilIsDead, fossilLanding, fossilRouteName } from '@/intel/fossils'
import { HUB_PATH, LISTEN_PATH, PIN_PATH, SHORT_PATH, gameFromSlug, legacyGuessTarget } from '@/guess/gameLinks'
import { PIN_EDITIONS, loadEditionText, pinEditionPath } from '@/guess/editions'
import { captureGameSource } from '@/composables/useGameAttribution'
import { wordLanguageGuard } from '@/games/wordCount/languageRoute'
import { flowStepOf } from '@/containers/schoolsFrontDoor'

// Schools views (lazy-loaded)
const DashboardView = () => import('@/views/schools/DashboardView.vue')
const TeachersView = () => import('@/views/schools/TeachersView.vue')
const StudentsView = () => import('@/views/schools/StudentsView.vue')
const TeacherDashboard = () => import('@/views/schools/TeacherDashboard.vue')
// THE LENS: the node-scoped Insight Engine — mounted at the old analytics
// URLs (the URLs live, the old-school analytics page died).
const NodeInsightsView = () => import('@/views/admin/NodeInsightsView.vue')
const SettingsView = () => import('@/views/schools/SettingsView.vue')
const SchoolsView = () => import('@/views/schools/SchoolsView.vue')
const SetupView = () => import('@/views/schools/SetupView.vue')
// HANDBOOK (2026-09-07) — the compiled map of every capability, shared by the
// /schools and /org mounts: one component, one address per lane.
const HandbookView = () => import('@/views/schools/HandbookView.vue')
const SupportView = () => import('@/views/schools/SupportView.vue')
const SchoolsInboxView = () => import('@/views/schools/InboxView.vue')
// THE INTELLIGENCE SURFACE — the question pages. They ride AdminContainer,
// the ONE shell over SSi's internal surfaces (Tom's ruling 2026-09-10:
// "share"), whose bar carries the seven questions.
const NotYetBuiltView = () => import('@/views/intel/NotYetBuiltView.vue')
const INTEL_VIEWS: Record<string, () => Promise<unknown>> = {
  business: () => import('@/views/intel/BusinessView.vue'),
  campaigns: () => import('@/views/intel/CampaignsView.vue'),
  learning: () => import('@/views/intel/LearningView.vue'),
  content: () => import('@/views/intel/ContentView.vue'),
  schools: () => import('@/views/intel/SchoolsView.vue'),
  'this-week': () => import('@/views/intel/ThisWeekView.vue'),
  working: () => import('@/views/intel/WorkingView.vue'),
  person: () => import('@/views/intel/PersonView.vue'),
}
// THE FOSSILS — the old admin pages a question replaces. Each renders only
// while a question it serves is unbuilt; the day the last one is built the
// same path becomes a redirect into the question, scope preserved. The rule
// lives in @/intel/fossils.ts and intel/grammar.test.ts enforces it, so a
// question cannot be built without killing the page it replaces.
// Only the fossils still ALIVE keep a loader: a dead one's view is deleted
// with it, and the grammar test refuses a live fossil with no loader.
const FOSSIL_VIEWS: Record<string, () => Promise<unknown>> = {
  stats: () => import('@/views/admin/AdminStatsView.vue'),
}
const FOSSIL_META: Record<string, { title: string; description: string }> = {
  stats: { title: 'Stats', description: 'Insight Engine boards — lifecycle, rates, content, ops' },
}
function fossilRoute(f: (typeof FOSSILS)[number]): RouteRecordRaw {
  if (fossilIsDead(f)) {
    return { path: f.path, redirect: (to) => fossilLanding(f, to.query as Record<string, unknown>, to.params as Record<string, unknown>) }
  }
  return {
    path: f.path,
    name: fossilRouteName(f),
    component: FOSSIL_VIEWS[f.path],
    meta: FOSSIL_META[f.path],
  }
}
const fossilRoutes: RouteRecordRaw[] = FOSSILS.map(fossilRoute)
// THE VIEW — the one recursive node home (archive/docs-retired-2026-08-24/THE-VIEW.md)
const NodeHomeView = () => import('@/views/admin/NodeHomeView.vue')
// Referenced ONLY from the INSTITUTIONAL_PURCHASE_IN_BUILD branches below, so a
// store build drops this dynamic import along with them.
const UpgradeView = () => import('@/views/schools/UpgradeView.vue')
// Teach (private tutor) views
const TeachDashboard = () => import('@/views/teach/TeachDashboard.vue')
const WithTeacher = () => import('@/views/teach/WithTeacher.vue')
const ParentPay = () => import('@/views/ParentPay.vue')
// Onboarding — the three signup doors (/schools1, /schools2, /tutors)
const Onboarding = () => import('@/views/onboarding/Onboarding.vue')

// The member-surface guard, shared by /schools and the top-level /org node
// surface — they are the SAME shell (SchoolsContainer), so a leader arriving
// by either door gets the same role priming and the same bounces.
// Parent-level so a deep-link (e.g. /schools/analytics, /org/:id/insights)
// primes the role cache before the container's gate runs. The
// platform-subscription gate itself (lever-3) is enforced in SchoolsContainer,
// which wraps EVERY child route — it's async (loads platform_status), and a
// router guard can't resolve it synchronously, so the container is the right
// place. This guard just makes sure the role cache is restored first (no flash
// of wrong state).
const memberSurfaceGuard: NavigationGuardWithThis<undefined> = (to, from, next) => {
  // Guided-mission deep link (?mission=<id>, dev/staging-gated): primes the
  // demo persona's role cache BEFORE the checks below so the mission's
  // signed-out visitor isn't bounced. The demo world itself is arranged in
  // SchoolsContainer setup (activatePendingMission), after setSchoolsClient.
  // Loaded only when a mission is actually named (job #763): the mission
  // definitions pull the schools data composables, and a static import here
  // put them in the entry chunk every learner downloads. A failed load goes
  // to next(err), so router.onError's stale-chunk recovery sees it.
  if (typeof to.query.mission !== 'string') return memberSurfaceChecks(to, from, next)
  import('@/missions/useMission')
    .then(({ prepareMissionFromRoute }) => {
      prepareMissionFromRoute(to)
      memberSurfaceChecks(to, from, next)
    })
    .catch((err) => next(err))
}
const memberSurfaceChecks: NavigationGuard = (to, _from, next) => {
  const { canAccessAdmin, hasSchoolRole, isInitialized, restoreFromCache } = useUserRole()
  restoreFromCache()
  // An ssi_admin sees everything, so a school, class or Handbook link opened by
  // one reaches the page it names (job #663, finding 3 of the #658 route
  // check). This used to eject every ssi_admin with no school role to
  // /admin/structure, which lost the destination; the reason it gave, the
  // learner join-code wall, went when canAccessSchools came to include
  // ssi_admin, and the node home endpoint already answers an admin for any id.
  // One exception stays: an admin sent here BY the admin estate with the
  // destination in ?next= (useAdminGate's guest hand-off) is replayed straight
  // to it, because an admin whose role was already cached never reaches
  // SchoolsContainer's replay (job #34). It is checked to be an in-app admin
  // path, so nothing new is reachable through it.
  if (canAccessAdmin.value && !hasSchoolRole.value) {
    const replay = adminNextFromQuery(to.query as Record<string, unknown>)
    if (replay) return next(replay)
    return next()
  }
  // The front door's sign-up flows (/schools?step=new-school | teacher |
  // tutor, job #182) are for people who are NOT members yet, exactly like
  // /schools1, /redeem and /tutors whose panels they are; a signed-in learner
  // redeeming a teacher code must reach the flow, not be bounced to /.
  if (flowStepOf(to.path, to.query as Record<string, unknown>)) return next()
  // A user with a KNOWN role but NO school role is not a member.
  // Solo tutors have no `educational_role`, so they look identical to a
  // plain learner here — the role cache can't tell them apart. Rather than
  // let them fall onto the member-facing "no school access" wall (a dead
  // end — this is exactly what trapped Aran coming from /teach), bounce
  // them to the surface they belong to: a tutor (last on /teach) back to
  // /teach, anyone else to the learner home. Only act once the role cache
  // is initialized, so a first cold load (cache not yet primed) still
  // reaches the container, which has its own login/loading handling.
  if (isInitialized.value && !hasSchoolRole.value) {
    return next(lastDashboard() === 'teach' ? '/tutors/dashboard' : '/')
  }
  // Genuine member — remember it for the symmetric breadcrumb.
  rememberDashboard('schools')
  next()
}

const routes: RouteRecordRaw[] = [
  // Learning player (default). Listening and Script are the same route with
  // the mode in the address, `/listening` and `/script` (job #345,
  // nav/modeRoute): one record, so moving between Speaking and a mode keeps
  // the player mounted and its place, and the guards below, which act on the
  // bare `/` only, never fire on a mode link.
  {
    path: '/:mode(listening|script)?',
    name: 'player',
    component: PlayerContainer,
    meta: {
      title: 'Learn',
      hideAppEscape: true, // immersive player — its own flow, no shell escape
      scrollLock: true, // the player pins itself to the viewport: the document must not pan (style.css)
    },
    // TEACHERS OPEN WITH THE DASHBOARD (Tom, 2026-09-14 13:03Z, job #662:
    // "We need teacher accounts to open with the dashboard and not the
    // player"). On the app's FIRST navigation only, a cached `teacher` role
    // arriving at the bare player goes to /schools; every in-app Learn /
    // My player tap still reaches the player, so her own play is one
    // deliberate step away, never the default. Rule and scope in
    // composables/teacherLanding.ts. For teachers this supersedes the
    // 2026-07-24 ruling below; everyone else keeps it.
    beforeEnter: (to, from) => {
      const { effectiveEducationalRole, restoreFromCache } = useUserRole()
      restoreFromCache()
      const target = teacherLandingTarget({
        role: effectiveEducationalRole.value,
        isFirstNavigation: from === START_LOCATION,
        path: to.path,
        queryKeys: Object.keys(to.query).length,
      })
      if (target) return { path: target }
      // THE INSTALLED APP OPENING SIGNED OUT (job #129): never a stranger.
      // iOS keeps a Home Screen app's storage apart from Safari's, so a
      // teacher's first open of her new icon has no session. Skip the picker;
      // PlayerContainer opens the password sign-in and sends a school role on
      // to /schools. Rule and scope in platform/installedReturn.
      if (installedSignedOutFirstLoad({
        isFirstNavigation: from === START_LOCATION,
        path: to.path,
        queryKeys: Object.keys(to.query).length,
        standalone: isStandaloneWindow(),
        local: windowSignals().local,
      })) {
        installedSignInPending.value = true
        return true
      }
      // PICKER FIRST (Tom, 2026-09-23, job #784): a stranger's very first
      // load of the bare player goes to the course picker, so choosing a
      // course hides its download. Rule and scope in platform/pickerFirst.
      // Decided here, before the router resolves this route's component, so
      // the player chunk is not fetched on the picker's critical path.
      const picker = pickerFirstRedirect({
        isFirstNavigation: from === START_LOCATION,
        path: to.path,
        // The query the router is navigating to, not the window's: they are
        // the same on a first load, and the route is what a test can drive.
        signals: { ...windowSignals(), search: new URLSearchParams(Object.keys(to.query).map((k) => [k, ''])).toString() },
      })
      if (picker) {
        pickerFirstActive.value = true
        return { path: picker, replace: true }
      }
      return true
    },
    // Owner ruling 2026-07-24: everyone lands in the player by default,
    // regardless of role. /schools is somewhere you deliberately navigate to
    // (the Learn/Schools switcher) — not a hijack on login. This supersedes
    // the 2026-07-16 "staff home is the dashboard" ruling below, which was
    // already tried once (ca88e0a8) and reverted for an unrelated reason (a
    // z-index tap-shield bug that made the course chooser dead for ALL
    // users, root-caused and fixed independently in 7f67014a — still in
    // place, so that landmine is already cleared). Deep links to /schools/*
    // are untouched by this route; SchoolsContainer's own guard bounces a
    // non-member back to their surface, it just never fires FROM here.
  },
  // The course picker as a page of its own, for a stranger's very first load
  // (job #784, platform/pickerFirst). Nothing links here; the bare player
  // redirects to it once. Anyone arriving otherwise, or after choosing, is
  // sent back to the player.
  {
    path: PICKER_FIRST_PATH,
    name: 'first-picker',
    component: () => import('@/views/FirstPickerView.vue'),
    meta: { title: 'Choose your course', hideAppEscape: true },
    beforeEnter: () => (pickerFirstActive.value ? true : { path: '/', replace: true }),
  },
  // THE VIEW's member node surface — TOP-LEVEL (founder ruling 2026-08-02:
  // an org/workplace is NOT a schools feature, so it must not live under
  // /schools). ONE :id route serves groups, orgs, schools AND classes (the
  // home endpoint resolves whichever id it's given), and the server scopes a
  // leader to their own subtree — rail rooted at their top node, no admin
  // escape. Server-backed reads only (no client org-table queries — the
  // RLS-condition caution). Same shell/guard/gate as /schools; only the URL
  // and the org-lane wording differ. `/orgs` (plural, defined below among the
  // signup doors) is the self-serve ORG SIGNUP door (founder instruction
  // 2026-08-02) — a distinct route name ('onboard-org'), never this member
  // node surface, so an org's OWN members still only ever land on /org/:id.
  {
    path: '/org',
    component: SchoolsContainer,
    meta: { hideAppEscape: true }, // SchoolsContainer carries its own nav
    beforeEnter: memberSurfaceGuard,
    children: [
      {
        path: ':id',
        name: 'org-node-home',
        component: NodeHomeView,
        meta: { title: 'Organisation', description: 'Node home (member scope)', nodeSurface: true },
      },
      {
        // THE LENS at member scope — "See insights" on the member node home.
        path: ':id/insights',
        name: 'org-node-insights',
        component: NodeInsightsView,
        meta: {
          title: 'Insights',
          description: 'The Insight Engine scoped to this node (member scope)',
        },
      },
      {
        path: ':id/handbook',
        name: 'org-handbook',
        component: HandbookView,
        meta: {
          title: 'Handbook',
          description: 'Everything this dashboard can do, compiled from the same source that gates the live dashboard',
        },
      },
      ...(INSTITUTIONAL_PURCHASE_IN_BUILD ? [{
        // The org lane's billing door: the same UpgradeView, reached without
        // an org leader ever being shown a /schools URL.
        path: 'upgrade',
        name: 'org-upgrade',
        component: UpgradeView,
        meta: { title: 'Upgrade', description: 'Subscribe / manage seats' },
      } as RouteRecordRaw] : []),
    ],
  },
  // Schools dashboard routes
  {
    path: '/schools',
    component: SchoolsContainer,
    meta: { hideAppEscape: true }, // SchoolsContainer carries its own nav
    beforeEnter: memberSurfaceGuard,
    children: [
      {
        path: 'setup',
        name: 'schools-setup',
        component: SetupView,
        meta: {
          title: 'School setup',
          description: 'First-time onboarding wizard for school admins',
        },
      },
      {
        path: '',
        name: 'schools-dashboard',
        // RETIRED for school-scoped school_admins (nav unification, third
        // persona, 2026-07-30): SchoolsContainer redirects them to their
        // school's node home (/org/:schoolId). Stays live for
        // teachers and legacy no-school school_admin rows.
        component: DashboardView,
        meta: {
          title: 'Dashboard',
          description: 'Overview of school learning activity',
          // railFrame: SchoolsContainer wraps this flat view in the
          // WHERE-YOU-ARE rail (founder ruling 2026-07-31: the rail is
          // orientation — it never disappears; see useSchoolsRail.ts).
          railFrame: true,
        },
      },
      {
        path: 'teachers',
        name: 'teachers',
        // RETIRED for school-scoped school_admins (2026-07-30): teachers ARE
        // the school node's children — SchoolsContainer redirects to the node
        // home with the teachers lens. Live for legacy no-school rows.
        component: TeachersView,
        meta: {
          title: 'Teachers',
          description: 'Manage teachers and their classes',
          railFrame: true,
        },
      },
      {
        path: 'students',
        name: 'students',
        component: StudentsView,
        meta: {
          title: 'Students',
          description: 'View and manage student progress',
          railFrame: true,
        },
      },
      {
        path: 'classes',
        name: 'classes',
        component: TeacherDashboard,
        meta: {
          title: 'My Classes',
          description: 'Manage classes and start learning sessions',
          railFrame: true,
        },
      },
      {
        // THE CLASS TOOLS PAGE IS THE CLASS PAGE (job #999, Tom's ruling
        // 2026-09-16: six teacher-facing views of one class collapse to four,
        // and a teacher gets ONE class page). The tools — roster, co-teachers,
        // join link, rename, delete, the copy-play repair — are now the
        // **Manage class** section of the class node home at /org/:id, which
        // already led with the class's own figures. This URL lives on as a
        // redirect so old links, bookmarks and the admin lane's own
        // /admin/schools/:id/classes/:classId hop keep working; query and hash
        // ride along. The admin read-view collapsed the same way in July.
        path: 'classes/:id',
        redirect: (to) => ({ path: `/org/${to.params.id}`, query: to.query, hash: to.hash }),
      },
      {
        path: 'analytics',
        name: 'analytics',
        // The teacher-scoped Rate-compare insight tool, embedded in the schools
        // shell (its own TopNav + full-viewport scroll suppressed via `embedded`).
        // For a group-scoped govt_admin (2026-07-29) and a school-scoped
        // school_admin (2026-07-30) this URL is retired — SchoolsContainer
        // redirects it to their node's /org/:id/insights (nav
        // unification); it stays live for teachers.
        component: () => import('@/insight/TeacherInsightsView.vue'),
        props: { embedded: true },
        meta: {
          title: 'Analytics',
          description: 'Your class vs the average — the Rate-compare insight tool',
          railFrame: true,
        },
      },
      {
        path: 'settings',
        name: 'settings',
        component: SettingsView,
        meta: {
          title: 'Settings',
          description: 'School and account settings',
          railFrame: true,
        },
      },
      {
        path: 'handbook',
        name: 'schools-handbook',
        component: HandbookView,
        meta: {
          title: 'Handbook',
          description: 'Everything this dashboard can do, compiled from the same source that gates the live dashboard',
          railFrame: true,
        },
      },
      {
        // The support channel — one thread per school, admins only (Tom,
        // 2026-09-10). The route refuses a teacher server-side; the menu
        // entry is simply not shown to one.
        path: 'support',
        name: 'schools-support',
        component: SupportView,
        meta: {
          title: 'Support',
          description: 'Your school\'s conversation with SSi, inside the app',
          railFrame: true,
        },
      },
      {
        // The inbox (job #684): messages to this person — Support replies and
        // the copy-play notice with its undo. Every schools role has one.
        path: 'inbox',
        name: 'schools-inbox',
        component: SchoolsInboxView,
        meta: {
          title: 'Inbox',
          description: 'Messages sent to you, and the one-tap actions they carry',
          railFrame: true,
        },
      },
      {
        path: 'all',
        name: 'schools-list',
        // RETIRED for group-scoped leaders (nav unification, 2026-07-29): the
        // URL lives, the separate design dies — SchoolsContainer redirects a
        // govt_admin with a group_id to the node home with the schools lens
        // (mirrors the admin-group-schools redirect below). The flat list
        // still mounts for legacy no-group govt_admin rows only.
        component: SchoolsView,
        meta: {
          title: 'Schools',
          description: 'All schools in group (govt admin)',
        },
      },
      {
        // LEGACY (moved 2026-08-02 to top-level /org — see the route above).
        // The old member-mount URLs live on as redirects so invite links,
        // bookmarks and shared deep links keep working; query and hash ride
        // along, which matters for ?lens=, ?student= and ?mission=.
        path: 'org/:id',
        redirect: (to) => ({ path: `/org/${to.params.id}`, query: to.query, hash: to.hash }),
      },
      {
        path: 'org/:id/insights',
        redirect: (to) => ({
          path: `/org/${to.params.id}/insights`,
          query: to.query,
          hash: to.hash,
        }),
      },
      {
        path: 'play',
        name: 'schools-play',
        component: PlayerContainer,
        meta: {
          scrollLock: true, // player pinned to the viewport (style.css)
          title: 'Class session',
          description:
            'Run a class learning session — schools top bar stays above the player. ' +
            'Play-as-class ONLY (owner ruling 2026-08-06): staff self-practice goes to the ' +
            'immersive navless player at /, so a class-less arrival here is redirected there.',
        },
      },
      ...(INSTITUTIONAL_PURCHASE_IN_BUILD ? [{
        path: 'upgrade',
        name: 'schools-upgrade',
        component: UpgradeView,
        meta: { title: 'Upgrade', description: 'Subscribe / manage teacher seats' },
      } as RouteRecordRaw] : []),
    ],
  },
  // Tutor (freelancer) dashboard. ONE tutor namespace: /tutors is the sign-up
  // page (Onboarding door, defined below); /tutors/dashboard is the freelancer's
  // shell. Consolidated from the old /teach surface (removed 2026-06-20) — the
  // /teach name + the buggy /teach/setup signup are gone; signup is /tutors only.
  {
    path: '/tutors/dashboard',
    component: TeachContainer,
    meta: { hideAppEscape: true }, // TeachContainer carries its own nav
    // See the /schools guard note: primes the role cache; the platform gate
    // (1mo tutor trial → £15/mo) is enforced async in TeachContainer.
    beforeEnter: (_to, _from, next) => {
      const { restoreFromCache } = useUserRole()
      restoreFromCache()
      // Remember the tutor's surface so if they later land on /schools (stale
      // link, confused session) the /schools guard sends them back here rather
      // than dumping them on the member wall.
      rememberDashboard('teach')
      next()
    },
    children: [
      {
        path: '',
        name: 'teach-dashboard',
        component: TeachDashboard,
        meta: { title: 'Teach' },
      },
      ...(INSTITUTIONAL_PURCHASE_IN_BUILD ? [{
        path: 'upgrade',
        name: 'teach-upgrade',
        component: UpgradeView,
        meta: { title: 'Upgrade', description: 'Subscribe to your tutoring dashboard' },
      } as RouteRecordRaw] : []),
      {
        // Play-as-class for tutors — renders the player INSIDE TeachContainer so
        // the tutor nav stays above it (mirrors /schools/play in SchoolsContainer).
        path: 'play',
        name: 'teach-play',
        component: PlayerContainer,
        meta: {
          scrollLock: true, // player pinned to the viewport (style.css)
          title: 'Class session',
          description:
            'Run a class learning session — tutor nav stays above the player. Play-as-class ' +
            'ONLY (owner ruling 2026-08-06); a class-less arrival is redirected to /.',
        },
      },
    ],
  },
  // Back-compat: old /teach links / PWA shortcuts → the new /tutors namespace.
  // /teach/setup is retired — its signup now happens at the /tutors door.
  { path: '/teach', redirect: '/tutors/dashboard' },
  { path: '/teach/setup', redirect: '/tutors' },
  ...(INSTITUTIONAL_PURCHASE_IN_BUILD
    ? [{ path: '/teach/upgrade', redirect: '/tutors/dashboard/upgrade' } as RouteRecordRaw]
    : []),
  { path: '/teach/play', redirect: '/tutors/dashboard/play' },
  // A parent pays a class-joined pupil's £5 a month (job #102). Public: the
  // signed link is the capability; the parent needs no account.
  {
    path: '/pay-for/:token',
    name: 'parent-pay',
    component: ParentPay,
    meta: { title: 'Pay for your child' },
  },
  // Student attribution gateway (no auth required)
  {
    path: '/with/:code',
    name: 'with-teacher',
    component: WithTeacher,
    meta: { title: 'Learning with your teacher' },
  },
  // Signup doors — two roles (school / tutor); the offer is per-course (pricing_tier),
  // not per-door. Note: bare /schools is the school DASHBOARD, not a signup door.
  {
    path: '/schools1',
    name: 'onboard-school-1',
    component: Onboarding,
    props: { track: 'school' },
    meta: { title: 'Set up your school' },
  },
  // /schools2 RETIRED (founder ruling 2026-08-02) — one school door, not two.
  // The URL stays alive FOREVER as a pure redirect because external marketing
  // links point at it; query + hash are preserved (same pattern as the
  // /schools/org/:id → /org/:id redirects above). /schools2 was the
  // English-first door listing the full catalogue, so /schools1's course
  // dropdown was widened from the year-free set to the WHOLE catalogue
  // (Onboarding.vue targetOptions) — no course becomes unreachable.
  {
    path: '/schools2',
    redirect: (to) => ({ path: '/schools1', query: to.query, hash: to.hash }),
  },
  {
    path: '/tutors',
    name: 'onboard-tutor',
    component: Onboarding,
    props: { track: 'tutor' },
    meta: { title: 'Start teaching' },
  },
  // Self-serve org/workplace signup (founder instruction 2026-08-02) — the
  // neutral-dressing sibling of the two doors above. Provisions via the
  // 'org' track (api/onboarding/provision.ts): a root `groups` row + a
  // govt_admins leader row, then redirects to the leader's own /org/:id.
  {
    path: '/orgs',
    name: 'onboard-org',
    component: Onboarding,
    props: { track: 'org' },
    meta: { title: 'Set up your organisation' },
  },
  // THE SCHOOLS SHOWCASE (job #432): the public page a prospective school is sent, no sign-in,
  // no session, no API call. Its link preview is the build's /for-schools/index.html
  // (views/marketing/showcase/showcaseShell.ts), served by vercel.json. Not /schools*, which is
  // the signed-in dashboard and its guards.
  {
    path: '/for-schools',
    name: 'schools-showcase',
    component: () => import('@/views/marketing/showcase/SchoolsShowcaseView.vue'),
    meta: { title: 'SaySomethingin for schools', hideAppEscape: true },
  },
  // THE PUBLIC HANDBOOK is plain HTML, not a route of the app (job #686):
  // tools/handbook-web renders every entry to /handbook/… at build time, so
  // search engines and link previews read it without running the player. An
  // in-app navigation opens the matching topic in the app shell.
  {
    path: '/handbook/:rest(.*)*',
    name: 'public-handbook',
    component: { render: () => null },
    beforeEnter: (to, from) => {
      // Loaded here directly means the server had no such page: go to the
      // Handbook index (home, if that is the page missing) rather than reload
      // the same address for ever.
      if (from === START_LOCATION) {
        const miss = handbookMissTarget(to.fullPath)
        if (!miss.external) return miss.to
        window.location.assign(`${import.meta.env.BASE_URL}${miss.to.replace(/^\//, '')}`)
        return false
      }
      return { path: to.path.replace(/^\/handbook/, '/app-handbook'), query: to.query, hash: to.hash }
    },
    meta: { title: 'Handbook', hideAppEscape: true },
  },
  {
    path: '/app-handbook/:rest(.*)*',
    name: 'in-app-handbook',
    component: () => import('@/views/InAppHandbookView.vue'),
    meta: { title: 'Handbook', hideAppEscape: true, learnerNav: true },
  },
  // A LEARNER'S SHOW ME LINK (job #419·C): a support reply leads with
  // /show-me/<walk-id>. The walk is deferred and the reader taken to the page
  // it plays on, which claims it — the Handbook's two moves, nothing new
  // (walkthrough/learnerShowMe.ts). anyPersona: the link names one walk, so
  // whoever opens it, an admin checking a reply included, is shown it.
  {
    path: '/show-me/:walk',
    name: 'learner-show-me',
    component: { render: () => null }, // never rendered — beforeEnter always redirects
    beforeEnter: async (to) => {
      const id = String(to.params.walk)
      const [{ walkById, deferWalk }, { learnerShowMe }] = await Promise.all([
        import('@/walkthrough/useWalkthrough'),
        import('@/walkthrough/learnerShowMe'),
      ])
      const { useUserRole } = await import('@/composables/useUserRole')
      const { filterPersona } = await import('@/walkthrough/persona')
      const roles = useUserRole()
      roles.restoreFromCache()
      // Signed out or role unknown: no persona, the public Handbook rules apply.
      const known = roles.platformRole.value || roles.effectiveEducationalRole.value || roles.isViewingAs.value
      const persona = known ? filterPersona(roles.platformRole.value, roles.effectiveEducationalRole.value, roles.isViewingAs.value) : null
      const go = learnerShowMe(id, walkById(id), persona)
      if (go.external) {
        window.location.assign(`${import.meta.env.BASE_URL}${go.to.replace(/^\//, '')}`)
        return false
      }
      // A new address also wakes a kept player when its Library was opened
      // locally and the requested destination is already '/'.
      const target = { path: go.to.split('?')[0], query: { ...Object.fromEntries(new URLSearchParams(go.to.split('?')[1] ?? '')), ...(go.defer ? { showMe: `${id}-${Date.now()}` } : {}) }, replace: true }
      const destination = router.resolve(target).fullPath
      if (go.defer) deferWalk(go.defer, { anyPersona: true, canClaim: () => router.currentRoute.value.fullPath === destination })
      return target
    },
    meta: { title: 'Show me', hideAppEscape: true },
  },
  // PARTNER DOORS — one landing page per partner network, parameterised off
  // views/marketing/partners.ts (a second partner = one copy entry + one route
  // line, no new component). Deliberately UNLINKED from every nav and
  // noindex'd by the component: shareable by URL, not discoverable. Sells the
  // LIVE tutor model only and CTAs into /tutors — no affiliate offer appears
  // on it (that lane is undecided, founder exploration 2026-08-03).
  {
    path: '/znotes',
    name: 'partner-znotes',
    component: () => import('@/views/marketing/PartnerDoor.vue'),
    props: { partner: 'znotes' },
    meta: { title: 'Teach English with SSi', hideAppEscape: true },
  },
  // THE LANGUAGE-GUESSING GAMES — public, no login, no guard. Plays library clips only (human, Cartesia,
  // Azure voices); see views/marketing/GuessView.vue. One link per game, no chooser (job #742, Tom
  // 2026-10-04); More games goes to the hub at /guess (/games is Games night's, below). guess/gameLinks.ts
  // holds the slugs. An old share link (/guess?g=love&s=7) goes on to its game's own address.
  {
    path: '/guess',
    name: 'guess',
    component: () => import('@/views/marketing/GamesHubView.vue'),
    beforeEnter: (to) => legacyGuessTarget(to.query, false) ?? true,
    meta: { title: 'Games', hideAppEscape: true },
  },
  // PIN IT (job #798): the globe game, its own page; a static path, so it is matched before /guess/:game
  {
    path: PIN_PATH,
    name: 'guess-pin',
    component: () => import('@/views/marketing/PinItView.vue'),
    meta: { title: 'Pin it', hideAppEscape: true },
  },
  // PIN IT EDITIONS (job #941, Tom 2026-10-10): the same game in the player's own language, /guess/hin/pin-it; the
  // edition's words are fetched before the page shows, so it never opens in English. /guess/hin opens its Pin it.
  {
    path: `/guess/:edition(${PIN_EDITIONS.join('|')})/pin-it`,
    name: 'guess-pin-edition',
    component: () => import('@/views/marketing/PinItView.vue'),
    props: (to) => ({ edition: String(to.params.edition) }),
    meta: { title: 'Pin it', hideAppEscape: true },
  },
  { path: `/guess/:edition(${PIN_EDITIONS.join('|')})`, redirect: (to) => ({ path: pinEditionPath(String(to.params.edition)), query: to.query }) },
  // LISTEN (job #811): tap a country on the globe, hear I love you from there; static, so before /guess/:game
  {
    path: LISTEN_PATH,
    name: 'guess-listen',
    component: () => import('@/views/marketing/ListenView.vue'),
    meta: { title: 'Listen to the world', hideAppEscape: true },
  },
  // SHORT CHALLENGE LINK (job #951): /g/<code> reads the stored challenge and opens its game with it, as the long link would
  {
    path: `${SHORT_PATH}/:code`,
    name: 'guess-short',
    component: () => import('@/views/marketing/GamesHubView.vue'),
    beforeEnter: async (to) => {
      // Loaded here, not at the top: the challenge decoder reaches the whole game and d3 (~200 kB),
      // and every learner's boot used to download it before the first sound (job #300).
      const { lookupChallenge, shortTarget } = await import('@/guess/challenge')
      const text = await lookupChallenge(String(to.params.code))
      const t = text ? shortTarget(text, String(to.query.e ?? '')) : null
      return t ? { path: t.path, query: t.query, replace: true } : { path: HUB_PATH }
    },
    meta: { title: 'Which language is it?', hideAppEscape: true },
  },
  { path: '/guess/map', name: 'guess-map', redirect: (to) => legacyGuessTarget(to.query, true) ?? { path: HUB_PATH } },
  {
    path: '/guess/:game',
    name: 'guess-game',
    component: () => import('@/views/marketing/GuessView.vue'),
    beforeEnter: (to) => (gameFromSlug(to.params.game) ? true : { path: HUB_PATH }),
    props: (to) => ({ game: gameFromSlug(to.params.game) }),
    meta: { title: 'Which language is it?', hideAppEscape: true },
  },
  // The same game with the answers drawn as areas on a map (job #698): hear it, tap where it is spoken.
  {
    path: '/guess/:game/map',
    name: 'guess-game-map',
    component: () => import('@/views/marketing/GuessView.vue'),
    beforeEnter: (to) => (gameFromSlug(to.params.game) ? true : { path: HUB_PATH }),
    props: (to) => ({ game: gameFromSlug(to.params.game), mode: 'map' }),
    meta: { title: 'Map what you hear', hideAppEscape: true },
  },
  // Teacher / tutor insights — the calm single-widget Rate-compare view.
  // Top-level + un-gated so it opens in a browser with ?demo WITHOUT a teacher
  // login (the global admin guard only fires on /admin + /methodology). It is
  // scoped to THEIR class (or a learner within it) and shows nothing but the
  // entity-vs-average Rate-compare widget — the opposite of /admin/insights.
  {
    path: '/teacher-insights',
    name: 'teacher-insights',
    component: () => import('@/insight/TeacherInsightsView.vue'),
    meta: {
      title: 'Your class',
      description: 'Your class vs the average — the Rate-compare widget, teacher-framed',
      hideAppEscape: true, // carries the full TopNav, so no floating Back needed
    },
  },
  // Learner profile / mirror — founder-commissioned design build 2026-08-03.
  // PREVIEW: deliberately UNLINKED from every nav, which is the flag — nothing
  // learner-visible changes until it is wired in, so this can be tasted on dev
  // without touching a single shipped surface. Lives under App.vue's provides,
  // so it injects the app's existing course plumbing rather than duplicating it.
  {
    path: '/me',
    name: 'learner-profile',
    component: () => import('@/views/me/ProfileView.vue'),
    meta: {
      learnerNav: true, // the one bottom nav (job #46)
      title: 'You',
      description: 'Learner profile, mirror and plan — preview surface',
      hideAppEscape: true, // carries the bottom nav
    },
  },
  // The social layer, job 1 (#479, 2026-09-21): availability and presence.
  // UNLINKED from every nav on purpose, exactly as /me was — nothing
  // learner-visible changes until it is wired in, so Tom and Aran can taste it
  // on dev by URL. Lives under App.vue's provides so it injects the app's own
  // supabase client and active course rather than duplicating either.
  {
    // The call itself (job #484). The :pairId is the availability slot's row
    // id — a taken slot IS the pair, so there is no second identifier to
    // carry. Reached from Connect, never guessable into: the server refuses
    // anybody the row does not name.
    path: '/connect/:pairId',
    name: 'connect-call',
    component: () => import('@/views/social/CallView.vue'),
    meta: {
      learnerNav: true, // the one bottom nav (job #46)
      title: 'Talking',
      description: 'A two-minute voice conversation with another learner',
      hideAppEscape: true,
    },
  },
  // Talk to humans (plate A-373, Tom 2026-09-24): the social front door, the
  // Connect with a random learner, and games night. Study groups have their
  // own tray row below it (2026-09-24). Speaking rooms are parked, no door.
  {
    path: '/talk',
    name: 'talk-to-humans',
    component: () => import('@/views/social/TalkToHumansView.vue'),
    meta: {
      learnerNav: true, // the one bottom nav (job #46)
      title: 'Talk to humans',
      description: 'A conversation with a learner now, and games night',
      hideAppEscape: true, // carries the bottom nav
    },
  },
  // Games night (job #920, 2026-09-23): a scheduled hour of two-minute random
  // pairings with a game card, played on Connect's own call. Unlinked, by URL.
  {
    path: '/games',
    name: 'games-night',
    component: () => import('@/views/social/GamesNightView.vue'),
    meta: {
      learnerNav: true, // the one bottom nav (job #46)
      title: 'Games night',
      description: 'A scheduled hour of two-minute conversations with a game, a new partner each time',
      hideAppEscape: true, // carries the bottom nav
    },
  },
  // The learner's Brain (job #774, 2026-10-15): one node per LEGO learnt, the
  // walks back through earlier ones, a second language beside it. PROTOTYPE:
  // three design directions behind ?dir=a|b|c for Tom to pick from, unlinked
  // from every nav on purpose, exactly as /me was.
  {
    path: '/brain',
    name: 'learner-brain',
    component: () => import('@/views/brain/BrainView.vue'),
    meta: {
      learnerNav: true, // the one bottom nav, identical on every screen (job #256)
      hideAppEscape: true, // carries the bottom nav, whose back control is the way out
      title: 'Your brain',
      description: 'Your brain on a language, grown one piece at a time',
    },
  },
  // The phrase redesign on the learning journey (Tom, ask 425, job #249): per
  // course, every stretch Phrase v4 redesigned on the journey arc, the
  // learner's position on it, and old beside new LEGO by LEGO. Staff view,
  // reached from the Library row SSi admins see beside My brain.
  {
    path: '/phrases',
    name: 'phrase-redesign',
    component: () => import('@/views/admin/AdminPhraseRedesign.vue'),
    meta: {
      title: 'Phrase redesign',
      description: 'Old and new phrases along the learning journey',
    },
  },
  // How many words do you know? (Aran, job #513, 2026-10-08): the word-count
  // game. Plays signed out from a public link — acquisition first — so no
  // learnerNav and no social door: a guest reaches it cold. Unlinked, by URL.
  // One page per language since plan v2 step 1 (2026-10-09): /words is the top
  // page, a card per language; /words/<code> is that language's game, and
  // /words/<code>/join/:token the same screen holding a friend's invite. A code
  // that is unknown or not yet playable goes to the top page. The registry is
  // games/wordCount/languages.
  {
    path: '/words',
    name: 'word-count-home',
    component: () => import('@/views/words/WordsHomeView.vue'),
    meta: {
      title: 'How many words do you know?',
      description: 'A text-only vocabulary game: pick a language and its map fills in as you play',
      hideAppEscape: true, // the screen carries its own way into the app
    },
  },
  // Demo only (Aran, 2026-10-09): the ten exit animations for the wrong options,
  // shown on a mock answer grid before any of them go into the game. Unlinked.
  // A static segment, so it outranks /words/:lang.
  {
    path: '/words/exits',
    name: 'word-count-exits',
    component: () => import('@/views/words/WordExitsShowcase.vue'),
    meta: {
      title: 'Word game exit animations',
      description: 'Demo of exit animations for the Welsh word-count game',
      hideAppEscape: true,
    },
  },
  // The player's profile (Aran, job #610, 2026-10-09): languages, maps, groups,
  // standing and Log out. Every /words page links here when signed in. Static,
  // so it outranks /words/:lang.
  {
    path: '/words/me',
    name: 'word-count-me',
    component: () => import('@/views/words/WordsMeView.vue'),
    meta: {
      title: 'Your profile — How many words do you know?',
      description: 'Your languages, maps and groups in the word game',
      hideAppEscape: true,
    },
  },
  // The English editions' front door (job #856, the India promotion): "How many English words do you
  // know?" in each of the ten eng_for_* languages. Static, so it outranks /words/:lang.
  {
    path: '/words/english',
    name: 'word-count-english',
    component: () => import('@/views/words/WordsEnglishView.vue'),
    meta: {
      title: 'How many English words do you know?',
      description: 'A vocabulary game for speakers of Hindi, Tamil and eight more languages',
      hideAppEscape: true,
    },
  },
  // Invite links sent before languages had pages: every group then was Welsh.
  {
    path: '/words/join/:token',
    redirect: (to) => ({ path: `/words/cym/join/${to.params.token}`, query: to.query }),
  },
  {
    path: '/words/:lang',
    name: 'word-count',
    component: () => import('@/views/words/WordCountView.vue'),
    beforeEnter: wordLanguageGuard,
    meta: {
      title: 'How many words do you know?',
      description: 'A text-only vocabulary game with a map that fills in as you play',
      hideAppEscape: true, // the screen carries its own way into the app
    },
  },
  {
    path: '/words/:lang/join/:token',
    name: 'word-count-join',
    component: () => import('@/views/words/WordCountView.vue'),
    beforeEnter: wordLanguageGuard,
    meta: {
      title: 'How many words do you know?',
      description: 'Join a friend\'s group in the word-count game',
      hideAppEscape: true, // the screen carries its own way into the app
    },
  },
  {
    path: '/connect',
    name: 'connect',
    component: () => import('@/views/social/ConnectView.vue'),
    meta: {
      learnerNav: true, // the one bottom nav (job #46)
      title: 'Talk to someone',
      description: 'Declare when you are free to talk, and take a slot somebody else declared',
      hideAppEscape: true, // carries the bottom nav
    },
  },
  // Study groups (job #517, 2026-09-21): the social layer's second half — a
  // room that keeps going between conversations, rather than one conversation.
  // Reached from the mode tray and from Connect, on the same audience gate.
  {
    // THE INVITE (Tom, 2026-09-24): no directory, so a member's link is a way
    // in. The server previews it and re-checks every rule on the tap.
    path: '/groups/invite/:token',
    name: 'study-group-invite',
    component: () => import('@/views/social/StudyGroupInviteView.vue'),
    meta: {
      learnerNav: true, // the one bottom nav (job #46)
      title: 'Study group invite',
      description: 'Join a study group a learner invited you to',
      hideAppEscape: true, // carries the bottom nav
    },
  },
  {
    // THE CHANNEL. The :id is the group row; the server refuses anybody who is
    // not an active member of it, and tells a removed learner why, once.
    path: '/groups/:id',
    name: 'study-group',
    component: () => import('@/views/social/StudyGroupView.vue'),
    meta: {
      learnerNav: true, // the one bottom nav (job #46)
      title: 'Study group',
      description: 'A small group of learners at about your level',
      hideAppEscape: true, // carries the bottom nav
    },
  },
  {
    path: '/groups',
    name: 'study-groups',
    component: () => import('@/views/social/StudyGroupsView.vue'),
    meta: {
      learnerNav: true, // the one bottom nav (job #46)
      title: 'Study groups',
      description: 'Join a study group and see the ones you are in',
      hideAppEscape: true, // carries the bottom nav
    },
  },
  // Speaking study ROOMS (job #752, 2026-09-22) — their own product, not study
  // groups: white belt and up, adults only, six seats, and all a room shows is
  // its total of speaking attempts and your own count.
  {
    path: '/rooms/:id',
    name: 'speaking-room',
    component: () => import('@/views/social/SpeakingRoomView.vue'),
    meta: {
      learnerNav: true, // the one bottom nav (job #46)
      title: 'Speaking room',
      description: 'Practise the course alongside others; the room counts every sentence you build',
      hideAppEscape: true, // carries the bottom nav
    },
  },
  {
    path: '/rooms',
    name: 'speaking-rooms',
    component: () => import('@/views/social/SpeakingRoomsView.vue'),
    meta: {
      learnerNav: true, // the one bottom nav (job #46)
      title: 'Speaking rooms',
      description: 'Join a speaking room in the language you are learning',
      hideAppEscape: true, // carries the bottom nav
    },
  },
  {
    // The learner inbox (job #684): reached from the More page and the
    // Library card. Never from Settings.
    path: '/me/inbox',
    name: 'learner-inbox',
    component: () => import('@/views/me/InboxView.vue'),
    meta: {
      learnerNav: true, // the one bottom nav (job #46)
      title: 'Inbox',
      description: 'Messages sent to you',
      hideAppEscape: true,
    },
  },
  // Admin panel
  {
    path: '/admin',
    component: AdminContainer,
    meta: { hideAppEscape: true }, // AdminContainer carries its own nav
    children: [
      {
        // Default /admin landing — the Structure surface (the org tree).
        path: '',
        redirect: '/admin/structure',
      },
      {
        // Canonical invites surface (2026-07-17 rethink): one create card
        // (org / direct / demo) + one live list, replacing the
        // creation/list halves of Access, Demos and Try Links. See
        // archive/docs-retired-2026-08-24/invites-redesign/DESIGN.md.
        path: 'invites',
        name: 'admin-invites',
        component: () => import('@/views/admin/AdminInvites.vue'),
        meta: { title: 'Invites', description: 'One primitive — who × where × what × limits; every link that lets someone in, real or demo' },
      },
      {
        // Old paths — kept working, not just bookmark hygiene. Ways-in
        // management now lives on /admin/structure (THE-MODEL.md §1.10).
        path: 'access',
        redirect: '/admin/structure',
      },
      {
        path: 'demos',
        redirect: '/admin/structure',
      },
      {
        path: 'demo-organisations',
        redirect: '/admin/structure',
      },
      {
        path: 'demo-schools',
        redirect: '/admin/structure',
      },
      {
        // People — the list you find one person in. A scope picker for the
        // questions, like Structure is for organisations; not a fossil.
        path: 'users',
        name: 'admin-users',
        component: () => import('@/views/admin/AdminUsers.vue'),
        meta: { title: 'People', description: 'Find one person by name, email or support id' },
      },
      ...fossilRoutes,
      {
        // The Handbook on the ADMIN surface. It has to exist here because an
        // ssi_admin is NOT a member of any school: memberSurfaceGuard (above)
        // bounces `canAccessAdmin && !hasSchoolRole` straight to
        // /admin/structure, so the Handbook chip on /admin/groups/:id used to
        // point at /schools/handbook and land the admin back on Structure
        // (founder report 2026-09-09). Same view, same compiled pack — only
        // the surface differs.
        path: 'handbook',
        name: 'admin-handbook',
        component: HandbookView,
        meta: {
          title: 'Handbook',
          description: 'Everything this dashboard can do, compiled from the same source that gates the live dashboard',
        },
      },
      {
        // Every Show me walk in the pack, launched the way the Handbook
        // launches one, for an SSi admin (job #653).
        path: 'show-me',
        name: 'admin-show-me',
        component: () => import('@/views/admin/AdminShowMe.vue'),
        meta: { title: 'Show me', description: 'Every Show me walkthrough, played on its own page in the demo school' },
      },
      {
        path: 'entitlements',
        redirect: '/admin/structure',
      },
      {
        path: 'try-links',
        redirect: '/admin/structure',
      },
      {
        // Support (job #28): every in-app report a learner has sent us, from
        // both postboxes, with the reply. Unanswered first.
        path: 'support',
        name: 'admin-support',
        component: () => import('@/views/admin/AdminSupport.vue'),
        meta: { title: 'Support', description: 'Every report sent from inside the app, and the reply' },
      },
      {
        // Admin-to-learner messaging (job #821): one composer, three audiences.
        path: 'messages',
        name: 'admin-messages',
        component: () => import('@/views/admin/AdminMessages.vue'),
        meta: { title: 'Messages', description: 'Tell learners what has gone live — one course, one learner, or everyone' },
      },
      {
        // Email marketing (job #509, docs/DECISIONS.md 2026-10-07): Aran writes the mail on his phone,
        // picks a segment, tests it to himself; only Tom can approve a send to a list, enforced on the
        // server. Results land on /intel/business, not here.
        path: 'email',
        name: 'admin-email',
        component: () => import('@/views/admin/AdminEmail.vue'),
        meta: { title: 'Email', description: 'Marketing email: write it, choose who gets it, send it once Tom has approved' },
      },
      {
        path: 'email/:id',
        name: 'admin-email-campaign',
        component: () => import('@/views/admin/AdminEmailCampaign.vue'),
        meta: { title: 'Email', description: 'Write one marketing email, preview it, test it, ask Tom to approve it' },
      },
      {
        path: 'partners',
        name: 'admin-partners',
        component: () => import('@/views/admin/AdminPartners.vue'),
        meta: { title: 'Partners', description: 'Country contacts who hand out capped Premium tester codes' },
      },
      {
        // Games stats (job #627): players, hours, and what the games brought in.
        path: 'games',
        name: 'admin-games',
        component: () => import('@/views/admin/AdminGames.vue'),
        meta: { title: 'Games', description: 'Players, hours played, and the courses and subscriptions the games brought in' },
      },
      {
        // Word game flags (job #790): "None of these is correct" reports, to accept, overrule or dismiss.
        path: 'word-flags',
        name: 'admin-word-flags',
        component: () => import('@/views/admin/AdminWordFlags.vue'),
        meta: { title: 'Word flags', description: 'Review the word pairs players flagged as wrong' },
      },
      {
        // Build-mode content proofing (job #321): ?topic=<id>, edits submitted once to build_content_proofs.
        path: 'build-proof',
        name: 'admin-build-proof',
        component: () => import('@/views/admin/AdminBuildProof.vue'),
        meta: { title: 'Build proof', description: 'Proof Build-mode Welsh conversations line by line and submit your edits' },
      },
      {
        // Structure — the org tree IS the page (2026-07-17 consolidation:
        // Setup's Groups/Schools/Staff/Entitlements tabs dissolved into one
        // tree with node facets; ways-in management lives on the node panel —
        // THE-MODEL.md §1.10).
        path: 'structure',
        name: 'admin-structure',
        component: () => import('@/views/admin/AdminStructure.vue'),
        meta: { title: 'Structure', description: 'The org tree — groups, schools, staff and entitlements at the node they belong to' },
      },
      {
        // Old Setup console path — Setup dissolved into Structure.
        path: 'setup',
        redirect: '/admin/structure',
      },
      {
        path: 'schools',
        redirect: '/admin/structure',
      },
      {
        // THE SCRIPT OF THE SCRIPT (job #612) — the whole Script mode order
        // for one language, walked by the live generator and rendered as a
        // table, each item carrying its conventions and a thumbnail that plays
        // its strokes (job #620). Read-only: it executes nothing and changes
        // nothing.
        path: 'script-ladder',
        name: 'admin-script-ladder',
        component: () => import('@/views/admin/AdminScriptLadder.vue'),
        meta: { title: 'Script ladder', description: 'The whole Script mode sequence for one language, dealt by the live generator' },
      },
      {
        // Draft videos from tools/video for SSi admins to watch on a phone
        // (job #841). The private admin-videos bucket is read only by
        // /api/admin/videos, admins only. Not the Handbook: no video there.
        path: 'videos',
        name: 'admin-videos',
        component: () => import('@/views/admin/AdminVideos.vue'),
        meta: { title: 'Videos', description: 'Draft videos from the video pipeline, admins only' },
      },
      {
        path: 'methodology',
        name: 'admin-methodology',
        component: () => import('@/views/admin/AdminMethodology.vue'),
        meta: { title: 'Measuring progress', description: 'Methodology papers and demos' },
      },
    ],
  },
  // ═══════════════════════════════════════════════════════════════════
  // THE INTELLIGENCE SURFACE — the ten questions.
  //
  // docs/delivery-side-intelligence-surface.md is the frame; every route here
  // is generated from @/intel/questions.ts, which is the ONE place the ten are
  // written down. One route per question is a rule the design makes checkable,
  // and generating the routes from the list is what makes it true rather than
  // remembered.
  //
  // A question whose page is not built yet still has its route and still sits
  // in the bar, rendering the honest not-yet card. The frame is ten from the
  // first day.
  // ═══════════════════════════════════════════════════════════════════
  {
    path: '/intel',
    component: AdminContainer,
    meta: { hideAppEscape: true },
    children: [
      // The surface opens on the business numbers (job #585, Tom 2026-09-21).
      { path: '', redirect: '/intel/this-week' },
      // The eleven became seven on 2026-09-27 (job #459). Every retired
      // page's address lands on the page its content moved to, query kept.
      // Listed BEFORE the questions: the walkthrough compiler reads both maps
      // as one wildcard route and gives a tie to the later record, which must
      // be the pages that render.
      ...Object.entries(RETIRED_QUESTIONS).map(([from, to]): RouteRecordRaw => ({
        path: from,
        redirect: (r) => ({ path: `/intel/${to}`, query: r.query }),
      })),
      ...QUESTIONS.map((q) => ({
        path: q.slug,
        name: `intel-${q.slug}`,
        component: q.built ? INTEL_VIEWS[q.slug] : NotYetBuiltView,
        meta: { title: q.tab, description: q.question },
      })),
    ],
  },
  // SSi HQ on its own (Aran, 2026-09-27, job #489). The band also sits at the
  // top of /intel/business, which is admin-only; this is the narrow door for a
  // team member with no admin role (the Chair), carrying HQ and nothing else.
  // Deliberately NOT under /intel, so the admin gate below does not bounce it:
  // /api/intel/hq decides, answering only a login on the hq_access list.
  // Landing end of scripts/mint-signin-link.mjs: spends a one-time token for Tom, then moves on. Staging only (the view checks the host).
  {
    path: '/signin-link',
    name: 'signin-link',
    component: () => import('@/views/SigninLink.vue'),
    meta: { title: 'Signing in' },
  },
  // THE PUBLIC TRY IT (job #523): the Handbook's Try it button lands here; the page asks the
  // server for a private sandbox and signs the visitor in. Staging only (server answers 404 elsewhere).
  {
    path: '/try-it',
    name: 'try-it',
    component: () => import('@/views/TryIt.vue'),
    meta: { title: 'Try it', hideAppEscape: true },
  },
  // A volunteer reviewer's word-flag queue, one language (Aran, 2026-10-15, job #660). Deliberately
  // NOT under /admin, so the admin gate above never bounces a volunteer: /api/words/review decides,
  // answering only the languages granted to the signed-in address.
  {
    path: '/review/words/:lang',
    name: 'review-words',
    component: () => import('@/views/review/ReviewWordsView.vue'),
    meta: { title: 'Review words', hideAppEscape: true },
  },
  {
    path: '/hq',
    name: 'hq',
    component: () => import('@/views/intel/HqView.vue'),
    meta: { title: 'SSi HQ' },
  },
  // The weekly report and its Print report button (job #603). Outside the admin gate for the
  // same reason as /hq: Nick reads it from the HQ staff list, not an admin role.
  // /api/intel/weekly-report decides — an SSi admin or an HQ-listed login — and returns counts only.
  {
    path: '/report/weekly',
    name: 'weekly-report',
    component: () => import('@/views/report/WeeklyReportView.vue'),
    meta: { title: 'Weekly report' },
  },
  // Admin read-views — view a specific school's dashboard as ssi_admin
  // without impersonating. useSchoolContext is populated from the :id
  // route param; queries still run as the real admin.
  {
    path: '/admin/schools/:id',
    component: AdminSchoolsContainer,
    meta: { hideAppEscape: true }, // carries AdminTopBar — the floating Back pill overlapped it
    children: [
      {
        // THE VIEW (archive/docs-retired-2026-08-24/THE-VIEW.md): the school's landing IS node home —
        // same recursive page as every other level. Deep school tools stay
        // at the sibling sub-routes below.
        path: '',
        name: 'admin-school-dashboard',
        component: NodeHomeView,
        meta: { title: 'School Home', description: 'Node home for a school', nodeSurface: true },
      },
      {
        path: 'classes',
        name: 'admin-school-classes',
        component: TeacherDashboard,
        meta: { title: 'School Classes' },
      },
      {
        // The admin "Class tools" page is DEAD (founder ruling 2026-07-19):
        // it duplicated the class node home with zero admin-usable verbs.
        // The URL survives as a redirect so old links never 404.
        path: 'classes/:classId',
        name: 'admin-school-class-detail',
        redirect: (to) => ({ path: `/admin/classes/${to.params.classId}` }),
      },
      {
        path: 'students',
        name: 'admin-school-students',
        component: StudentsView,
        meta: { title: 'School Students' },
      },
      {
        path: 'teachers',
        name: 'admin-school-teachers',
        component: TeachersView,
        meta: { title: 'School Teachers' },
      },
      {
        path: 'analytics',
        name: 'admin-school-analytics',
        component: NodeInsightsView,
        meta: { title: 'School Insights', description: 'The Insight Engine scoped to this school' },
      },
    ],
  },
  // Admin read-view for groups (cross-schools, govt_admin-scope queries)
  {
    path: '/admin/groups/:id',
    component: AdminGroupContainer,
    meta: { hideAppEscape: true }, // carries AdminTopBar — no floating Back pill on top
    children: [
      {
        // THE VIEW (archive/docs-retired-2026-08-24/THE-VIEW.md): the group's landing IS node home.
        path: '',
        name: 'admin-group-dashboard',
        component: NodeHomeView,
        meta: { title: 'Group Home', description: 'Node home for a group', nodeSurface: true },
      },
      {
        // The old Full-schools list — the URL lives, the separate design
        // dies: node home with the All-schools lens preselected.
        path: 'schools',
        name: 'admin-group-schools',
        redirect: (to) => ({ path: `/admin/groups/${to.params.id}`, query: { lens: 'schools' } }),
      },
      {
        path: 'analytics',
        name: 'admin-group-analytics',
        component: NodeInsightsView,
        meta: { title: 'Group Insights', description: 'The Insight Engine scoped to this group' },
      },
    ],
  },
  // Standalone admin read-views
  {
    // THE VIEW (archive/docs-retired-2026-08-24/THE-VIEW.md): class level gets the same node home —
    // map rail, identity (lead + co-teachers, read-only), students as
    // children (with the full teaching data in-row). Deliberately the SAME
    // container + view pair as /admin/groups/:id so drilling group → class
    // reuses the mounted surface (one continuous map, no repaint — founder
    // ruling 2026-07-19). This IS the class page for admins — the old
    // "Class tools" page is dead (founder ruling 2026-07-19).
    path: '/admin/classes/:id',
    component: AdminGroupContainer,
    meta: { hideAppEscape: true },
    children: [
      {
        path: '',
        name: 'admin-class-detail',
        component: NodeHomeView,
        meta: { title: 'Class Home (Admin)', nodeSurface: true },
      },
    ],
  },
  {
    // THE DISPLAY LAB (job #26) — a dozen renderings of one rate-compare
    // series, side by side, with like / unsure / no per tile. Admin-gated by
    // the /admin prefix; a decision instrument, not a product surface.
    path: '/admin/insights-lab',
    name: 'admin-insights-lab',
    component: () => import('@/views/admin/AdminInsightsLabView.vue'),
    meta: { title: 'Insights display lab' },
  },
  {
    // THE LENS at class level — "See insights" on a class node home.
    path: '/admin/classes/:id/insights',
    name: 'admin-class-insights',
    component: () => import('@/views/admin/AdminClassInsights.vue'),
    meta: { title: 'Class Insights (Admin)' },
  },
  {
    // The individual learner page is DEAD (founder ruling 2026-07-19) — its
    // teacher-relevant content (journey, last-7-days) lives flat on the
    // student rows of the class node home. The URL survives as a redirect
    // so old links never 404.
    path: '/admin/users/:learnerId/progress',
    redirect: (to) => ({ path: `/admin/users/${to.params.learnerId}` }),
  },
  // Shareable redeem link. :code? is optional — a bare /redeem visit (e.g. a
  // teacher's whiteboard code, typed manually rather than clicked) drops into
  // RedeemCode.vue's manual code-entry step instead of 404ing.
  {
    path: '/redeem/:code?',
    name: 'redeem-code',
    component: () => import('@/views/RedeemCode.vue'),
    meta: { title: 'Redeem Code' },
  },
  // Group leader landing door (owner addendum 2026-07-13): a signup-page-
  // skinned entry to the govt_admin invite flow, matching the /schools1 /
  // /tutors landing-page convention. Reuses RedeemCode's redemption
  // machinery unchanged — /group?code=XYZ and /group/:code both work (the
  // code param is optional so the bare query form resolves too).
  {
    path: '/group/:code?',
    name: 'group-landing',
    component: () => import('@/views/RedeemCode.vue'),
    props: { variant: 'landing' },
    meta: { title: 'Bring SSi to your group' },
  },
  // The teacher return route (Tom's ruling 2026-09-02). A school admin mints a
  // short access code for a colleague our email cannot reach; this is where it
  // is spent. Deliberately the shortest path we could give it — the whole
  // point is that a person can read "/join" and eight characters down a phone
  // or off a printed slip. :code? is optional so both the tappable
  // /join/ABCD-EFGH and a bare /join with the code typed in resolve.
  {
    path: '/join/:code?',
    name: 'join-with-code',
    component: () => import('@/views/JoinWithCode.vue'),
    meta: { title: 'Sign in with your access code' },
  },
  // The funded-cohort door (Kai's spec, 2026-09-08). ONE link, never two: the
  // old system's separate under-25 link put people in the wrong cohort and
  // nobody could tell afterwards, so age is a tick on this page instead. Open
  // to signed-out visitors — the page reads what it is asking them to agree to
  // BEFORE it asks them to make an account.
  {
    path: '/enrol/:code?',
    name: 'org-enrolment',
    component: () => import('@/views/OrgEnrolment.vue'),
    meta: { title: 'Claim your free year' },
  },
  // Try link gateway (no auth required — zero-friction course preview)
  {
    path: '/try/:code',
    name: 'try-link',
    component: () => import('@/views/TryLinkGateway.vue'),
    meta: { title: 'Try SaySomethingin' },
  },
  // The framed marketing demo (no auth, no storage, no service worker). The
  // ONE surface the app allows to be framed, and only by saysomethingin.com —
  // see the /embed/(.*) header rule in vercel.json and platform/embedMode.ts.
  // hideAppEscape because the frame has no "out" but its own single deep link.
  {
    path: '/embed/demo',
    name: 'embed-demo',
    component: () => import('@/views/EmbedDemoView.vue'),
    meta: { title: 'SaySomethingin', hideAppEscape: true },
  },
  // Frozen board-report snapshot (no auth — capability-by-unguessability,
  // living-board-report-spec.md §5). Renders only the stored payload.
  {
    path: '/board/:code',
    name: 'board-snapshot',
    component: () => import('@/views/BoardSnapshotView.vue'),
    meta: { title: 'Board Report' },
  },
  // PWA install guide
  // Partner tester codes (job #384). Reached from Settings → Codes, which shows
  // the row only to a partner; api/partner/codes.ts is the gate, not this route.
  {
    path: '/partner',
    name: 'partner-codes',
    component: () => import('@/views/PartnerCodesView.vue'),
    meta: { title: 'Partner codes' },
  },
  // Affiliate links and codes (job #433). /r/<CODE> is normally rewritten to /?ref=<CODE> at
  // boot (platform/affiliateTouch); this route catches an in-app navigation to one. Bare /r is
  // where someone types the code an affiliate told them.
  {
    path: '/r/:code',
    name: 'affiliate-link',
    component: () => import('@/views/AffiliateCodeView.vue'),
    beforeEnter: async (to) => {
      const { recordTouch, touchFromSearch } = await import('@/platform/affiliateTouch')
      recordTouch(touchFromSearch(`?ref=${encodeURIComponent(String(to.params.code || ''))}`))
      return { path: '/', replace: true }
    },
  },
  {
    path: '/r',
    name: 'affiliate-code',
    component: () => import('@/views/AffiliateCodeView.vue'),
    meta: { title: 'Partner code' },
  },
  {
    path: '/install',
    name: 'install-guide',
    component: () => import('@/views/InstallGuide.vue'),
    meta: { title: 'Install App' },
  },
  // Methodology explainer pages — admin-gated initially. See
  // archive/docs-retired-2026-08-24/methodology/metrics-architecture.md §9 (Methodology explainer pages).
  // Spec-by-demonstration: each page renders a working visualisation of a
  // principle from the metrics-architecture spec using real (anonymised)
  // learner data. Settings toggles will link here to demystify what each
  // option measures and what the learner gets back from turning it on.
  {
    path: '/methodology',
    component: MethodologyContainer,
    children: [
      {
        path: '',
        name: 'methodology',
        component: () => import('@/views/methodology/MethodologyView.vue'),
        meta: { title: 'Methodology', description: 'Methodology explainer pages — admin' },
      },
      {
        path: 'empirical-baseline',
        name: 'methodology-empirical-baseline',
        component: () => import('@/views/methodology/EmpiricalBaselineView.vue'),
        meta: { title: 'Empirical baseline', description: 'Population distribution of practice hours with 30/100-hour anchors' },
      },
    ],
  },
  // Hidden power-user/support alias for the existing `?reset=1` recovery
  // mode (App.vue) — a *typed-in* URL must be speakable over the phone to a
  // stuck learner; query strings aren't. Deliberately not linked from any
  // UI copy (the boot-watchdog floor screen has its own self-contained "Fix
  // the app" button per Tom's ruling — this route is the support-channel
  // fallback, never something the app itself points users at). A hard
  // navigation, not a client-side route change, so the full reset flow
  // (App.vue's top-level check) actually runs.
  {
    path: '/reset',
    name: 'reset-alias',
    beforeEnter: () => {
      window.location.replace('/?reset=1')
      return false
    },
    component: { render: () => null }, // never rendered — beforeEnter always hard-redirects first
  },
  // Course links (job #124, platform/learnLink): /learn/<course_code> opens
  // that course. A cold load is rewritten in main.js before the router reads
  // it; this record catches the same link followed from inside the app — and
  // follows it with a HARD navigation, not a soft redirect (job #181, lane
  // review #159): the course a link names is read once at boot, so a soft
  // `/?course=eng_for_hin` while Spanish was mounted left Spanish playing
  // under a Hindi address. Same shape as the `/reset` alias above.
  {
    path: '/learn/:code?',
    name: 'learn-link',
    beforeEnter: (to) => {
      const qs = new URLSearchParams(
        Object.entries(to.query).flatMap(([k, v]) => (Array.isArray(v) ? v : [v]).filter((x): x is string => typeof x === 'string').map((x) => [k, x])),
      ).toString()
      followLearnLinkInApp(to.path, qs ? `?${qs}` : '', to.hash)
      return false
    },
    component: { render: () => null }, // never rendered — beforeEnter always hard-navigates first
  },
  // Catch-all redirect to player
  {
    path: '/:pathMatch(.*)*',
    redirect: '/',
  },
]

const router = createRouter({
  history: createWebHistory(),
  routes,
  scrollBehavior(to, from, savedPosition) {
    if (savedPosition) {
      return savedPosition
    }
    // Drilling within THE VIEW's node surface is movement inside ONE map —
    // keep the scroll where it is (no jump-to-top jolt between nodes).
    if (to.meta.nodeSurface && from.meta.nodeSurface) {
      return false
    }
    return { top: 0 }
  },
})

// Stale chunk recovery: after a deploy, the running tab still references
// old hashed chunk URLs that no longer exist on the CDN. Reload to the
// target path so the browser fetches a fresh index.html + current chunks.
router.onError((err, to) => {
  if (!isChunkLoadError(err)) return
  void recoverFromChunkError(to.fullPath, {
    read: (k) => sessionStorage.getItem(k),
    write: (k, v) => sessionStorage.setItem(k, v),
    purge: async () => { await unregisterAllServiceWorkers(); await clearAllCaches() },
    go: (path) => window.location.assign(path),
    say: showChunkFailureNotice,
  })
})

// A successful navigation means the (possibly freshly-reloaded) chunks loaded,
// so clear any stale-chunk reload guard for that path — a later deploy in the
// same session can then trigger recovery again.
router.afterEach((to) => {
  try { sessionStorage.removeItem('ssi-chunk-reload:' + to.fullPath) } catch { /* storage blocked */ }
})

// Guard admin + methodology routes — useUserRole is the single authority.
// Methodology pages are admin-gated initially (see metrics-architecture.md §9);
// individual pages may be opened to all learners later as we add a per-route
// `meta.public: true` flag, but for now everything under /methodology requires
// ssi_admin / god.
//
// Used to deny (bounce to '/') whenever canAccessAdmin was false — including
// a fresh browser with no cache yet, reading "don't know" as "no" and
// bouncing an about-to-resolve ssi_admin off every deep link. Now mirrors the
// /schools guard's own shape: only a role the cache actually KNOWS to be
// non-admin gets bounced here; an unresolved cache defers to AdminContainer,
// which gates rendering on the shared resolved-session gate and corrects
// (redirects) once resolution genuinely says non-admin.
router.beforeEach((to, _from, next) => {
  const requiresAdmin = to.path.startsWith('/admin') || to.path.startsWith('/intel') || to.path.startsWith('/methodology')
  if (!requiresAdmin) return next()
  const { canAccessAdmin, isInitialized, isRoleAuthoritative, restoreFromCache } = useUserRole()
  restoreFromCache()
  // A role that is only what localStorage remembered is not an ANSWER — it is
  // the last visit's answer, carrying no identity and no timestamp. Bouncing
  // on it threw a real ssi_admin off /admin/insights-lab on a phone whose
  // cache still said "learner", milliseconds before the DB said ssi_admin
  // (job #34). An unresolved-or-cached role defers to AdminContainer, which
  // renders nothing while it waits and redirects once resolution genuinely
  // says non-admin — so nothing is opened up to a non-admin here.
  if (isInitialized.value && isRoleAuthoritative.value && !canAccessAdmin.value) return next(staffHome())
  next()
})

// School accounts never reach the social layer (Tom, 2026-09-24, job #113).
// The tray hides the rows; this refuses the pages by URL, sending a school
// account to the player. The server's answer decides (useSocialDoor), and the
// server refuses every /api/social/* call on its own — this is not the lock.
router.beforeEach(async (to) => {
  // Release hold (config/releaseHolds.ts): a held feature's page is not in this release.
  if (isHeldPath(to.path)) return { path: '/', replace: true }
  // The store app has no tutor lane (platform/tutorLane.ts): tutors type bank
  // details for payouts there, and Play's Data safety form must not have to
  // declare payment info, so no page of it opens inside the app. On the web
  // this is never true.
  if (isClosedTutorLanePath(to.path)) return { path: '/', replace: true }
  // The store app opens no web purchase page (platform/paymentRoute.ts): a
  // school's or an organisation's seats, a parent's pay link. Play forbids any
  // in-app path to another payment method. /with/:code stays open, with no web
  // price shown. On the web this is never true.
  if (isClosedWebPurchasePath(to.path)) return { path: '/', replace: true }
  if (!isSocialPath(to.path)) return true
  return (await checkSocialDoor()) ? { path: '/', replace: true } : true
})

// Owner ruling 2026-08-06: entering the player ALWAYS gives the immersive,
// navless player at '/'. The two embedded play routes survive for play-as-class
// only — there the shell bar earns its place by naming WHICH class is live — and
// every launcher pushes a `?class=` query. An arrival without one is a stale
// bookmark or a hand-typed URL; a class-less wrapped player is precisely the
// two-doors-one-player inconsistency this removes, so send it to '/'.
//
// GLOBAL rather than per-route beforeEnter deliberately: a query-only change on
// the same record (dropping ?class= while staying on /schools/play) does NOT
// re-run beforeEnter, and that is exactly the case that would slip through.
router.beforeEach((to, _from, next) => {
  if (!EMBEDDED_PLAY_ROUTE_NAMES.includes(to.name as string)) return next()
  if (typeof to.query.class === 'string' && to.query.class) return next()
  next('/')
})

// The rest of the English strings (job #850). The entry carries only the
// learner's part of eng.json; the schools, org, teacher and insight namespaces
// are their own chunk. Every route but the player's first screens starts it in
// beforeEach, in parallel with the route's own chunk, and beforeResolve waits
// for it so a dashboard never paints a raw key. loadDeferredEnglish never
// rejects, so a failed fetch degrades to each string's fallback rather than a
// stuck navigation. The player and picker never wait for it: their strings are
// all in the boot part.
export const FIRST_SCREEN_ROUTE_NAMES = ['player', 'first-picker', 'embed-demo']
const needsDeferredEnglish = (name: unknown) => !FIRST_SCREEN_ROUTE_NAMES.includes(name as string)
// Which game sent this person in (job #513): any route reached with
// ?source=<game>-<language> is remembered, and stored against the learner the
// first time this browser is signed in (composables/useGameAttribution.ts).
router.beforeEach((to) => { captureGameSource(to.query, to.path) })
router.beforeEach((to) => { if (needsDeferredEnglish(to.name)) void loadDeferredEnglish() })
// An edition's words load on every arrival at its page, a change of :edition on the same record included (beforeEnter
// does not fire for that, so /guess/urd/pin-it -> /guess/hin/pin-it kept Urdu); job #979.
router.beforeResolve(async (to) => { if (to.name === 'guess-pin-edition') await loadEditionText(String(to.params.edition)).catch(() => null) })
router.beforeResolve((to) => (needsDeferredEnglish(to.name) ? loadDeferredEnglish() : undefined))

// Schools, admin and onboarding stay English unless the person chose a language.
router.afterEach((to) => { if (ENGLISH_ONLY_PATH.test(to.path)) dropInferredLocale() })

// Update document title on navigation
router.afterEach((to) => {
  const baseTitle = 'SSi'
  const pageTitle = to.meta.title as string | undefined
  document.title = pageTitle ? `${pageTitle} - ${baseTitle}` : baseTitle
})

export default router
